import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase-config";
import { collection, getDocs, where, query, orderBy, updateDoc, doc, getDoc } from "firebase/firestore";
import loginBg from '../assets/svg/login.svg';
import "./EmployeeDashboard.css";
import { AbsencePopup } from "./EmployeeAbsenceList";

function UnifiedNotificationBlock({ absenceNotifications, timesheetNotifications, onConsulterAbsence, onConsulterTimesheet }) {
  const totalNotifications = absenceNotifications.length + timesheetNotifications.length;
  
  if (totalNotifications === 0) return null;
  
  return (
    <div style={{marginBottom: 18}}>
      {/* Section Absences */}
      {absenceNotifications.length > 0 && (
        <div style={{
          background: '#fffde7', 
          borderRadius: 10, 
          padding: 16, 
          marginBottom: 12, 
          boxShadow: '0 2px 8px #ffe08255', 
          border: '1px solid #ffe082', 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'flex-start'
        }}>
          <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6}}>
            <span style={{fontSize: 20}}>✉️</span>
            <span style={{fontWeight: 600, fontSize: 17}}>Notification d'absence</span>
          </div>
          {absenceNotifications.map((notif, idx) => (
            <div key={notif.id} style={{fontSize: 13, color: '#444', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10}}>
              Notification de retour de demande d'absence
              <button style={{
                marginLeft: 8, 
                fontSize: 13, 
                background: '#ffeb3b', 
                color: '#333', 
                border: 'none', 
                borderRadius: 8, 
                padding: '2px 12px', 
                fontWeight: 600, 
                cursor: 'pointer'
              }} onClick={async () => await onConsulterAbsence(notif.id)}>Consulter</button>
            </div>
          ))}
        </div>
      )}

      {/* Section Feuilles de temps */}
      {timesheetNotifications.length > 0 && (
        <div style={{
          background: '#f3e5f5', 
          borderRadius: 10, 
          padding: 16, 
          marginBottom: 12, 
          boxShadow: '0 2px 8px #ce93d855', 
          border: '1px solid #ce93d8', 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'flex-start'
        }}>
          <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6}}>
            <span style={{fontSize: 20}}>📋</span>
            <span style={{fontWeight: 600, fontSize: 17}}>Notification de feuille de temps</span>
          </div>
          {timesheetNotifications.map((notif, idx) => (
            <div key={notif.id} style={{fontSize: 13, color: '#444', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10}}>
              {notif.isReminder 
                ? `Rappel : Votre feuille de temps de ${notif.monthName} ${notif.year} doit être signée`
                : `Votre feuille de temps de ${notif.monthName} ${notif.year} est prête à être signée`
              }
              <button style={{
                marginLeft: 8, 
                fontSize: 13, 
                background: '#9c27b0', 
                color: '#fff', 
                border: 'none', 
                borderRadius: 8, 
                padding: '2px 12px', 
                fontWeight: 600, 
                cursor: 'pointer'
              }} onClick={async () => await onConsulterTimesheet(notif.id)}>Consulter</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatDateFR(dateStr) {
  if (!dateStr) return '';
  const [yyyy, mm, dd] = dateStr.split('-');
  return `${dd}-${mm}-${yyyy}`;
}

function TimesheetPopup({ id, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const docRef = doc(db, "workSessionsMonth", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setData({ id: docSnap.id, ...docSnap.data() });
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des données:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          background: 'white',
          padding: 32,
          borderRadius: 12,
          textAlign: 'center'
        }}>
          Chargement...
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        padding: 32,
        borderRadius: 12,
        maxWidth: '95%',
        width: '95%',
        textAlign: 'center',
        '@media (max-width: 768px)': {
          maxWidth: '98%',
          width: '98%',
          padding: 20,
          margin: '10px'
        }
      }}>
        <h3 style={{marginBottom: 16}}>Feuille de temps à signer</h3>
        <div style={{fontSize: 14, color: '#444', marginBottom: 16}}>
          <div><b>{data.employeeName || data.employeeEmail}</b></div>
          <div>Période : {data.monthName} {data.year}</div>
          <div style={{fontSize: 12, color: '#888', marginTop: 4}}>
            ({formatDateFR(data.monthStart)} au {formatDateFR(data.monthEnd)})
          </div>
        </div>
        <div style={{fontSize: 13, color: '#666', marginBottom: 20}}>
          Votre feuille de temps a été validée par votre employeur et est prête à être signée.
        </div>
        <div style={{display: 'flex', gap: 12, justifyContent: 'center'}}>
          <button 
            style={{
              background: '#9c27b0',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '8px 16px',
              cursor: 'pointer',
              fontWeight: 600
            }}
            onClick={() => {
              onClose();
              navigate("/timesheet");
            }}
          >
            Aller aux feuilles de temps
          </button>
          <button 
            style={{
              background: '#666',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '8px 16px',
              cursor: 'pointer'
            }}
            onClick={onClose}
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

function EmployeeDashboardBoard() {
  const navigate = useNavigate();
  const [employeeName, setEmployeeName] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [timesheetNotifications, setTimesheetNotifications] = useState([]);
  const [showAbsencePopup, setShowAbsencePopup] = useState(null);
  const [showTimesheetPopup, setShowTimesheetPopup] = useState(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        console.log(`[EmployeeDashboardBoard] Récupération des données pour: ${user.email}`);
        
        try {
          // Utiliser une requête qui respecte les règles Firestore
          const employeeQuery = query(
            collection(db, "employees"),
            where("email", "==", user.email)
          );
          const employeeSnapshot = await getDocs(employeeQuery);
          
          if (!employeeSnapshot.empty) {
            const employee = employeeSnapshot.docs[0];
            const employeeData = employee.data();
            const fullName = `${employeeData.prenom} ${employeeData.nom}`;
            console.log(`[EmployeeDashboardBoard] Nom récupéré: "${fullName}"`);
            console.log(`[EmployeeDashboardBoard] Document ID: ${employee.id}`);
            setEmployeeName(fullName);
          } else {
            console.warn(`[EmployeeDashboardBoard] Aucun employé trouvé pour: ${user.email}`);
          }
          
        } catch (error) {
          console.error(`[EmployeeDashboardBoard] Erreur lors de la récupération:`, error);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchNotif = async () => {
      const user = auth.currentUser;
      if (!user) return;
      
      try {
        // Récupérer les notifications d'absences
        const absencesQuery = query(
          collection(db, "absences"),
          where("employeeEmail", "==", user.email),
          where("vuParEmploye", "==", false),
          where("status", "in", ["Validé", "Refusé"]),
          orderBy("createdAt", "desc")
        );
        const absencesSnap = await getDocs(absencesQuery);
        const absenceNotifs = absencesSnap.docs.map(doc => ({ 
          id: doc.id, 
          type: 'absence',
          ...doc.data() 
        }));

        // Récupérer les notifications de feuilles de temps MENSUELLES
        const timesheetQuery = query(
          collection(db, "workSessionsMonth"),
          where("employeeEmail", "==", user.email),
          where("status", "==", "En attente de signature salarié"),
          where("signed", "==", false),
          orderBy("monthStart", "asc")
        );
        const timesheetSnap = await getDocs(timesheetQuery);
        const timesheetNotifs = timesheetSnap.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            type: 'timesheet',
            monthStart: data.monthStart,
            monthEnd: data.monthEnd,
            monthName: data.monthName,
            year: data.year,
            adminValidatedAt: data.adminValidatedAt,
            isReminder: data.timesheetReminderCreated || false,
            ...data
          };
        });

        setNotifications(absenceNotifs);
        setTimesheetNotifications(timesheetNotifs);
      } catch (error) {
        console.error("Erreur lors de la récupération des notifications:", error);
      }
    };
    fetchNotif();
  }, []);

  const handleConsulterAbsence = async (id) => {
    await updateDoc(doc(db, "absences", id), { vuParEmploye: true });
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    setShowAbsencePopup(id);
  };

  const handleConsulterTimesheet = async (id) => {
    await updateDoc(doc(db, "workSessionsMonth", id), { timesheetNotificationVu: true });
    setTimesheetNotifications(prev => prev.filter(n => n.id !== id));
    setShowTimesheetPopup(id);
  };

  return (
    <div className="dashboard-board-container" style={{
      minHeight: '100vh',
      background: `url(${loginBg}) center/cover no-repeat`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0
    }}>
      <div className="dashboard-board-content" style={{
        background: 'rgba(255,255,255,0.85)',
        borderRadius: 20,
        padding: 32,
        boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
        minWidth: 320,
        maxWidth: 400,
        minHeight: 340,
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
        position: 'relative'
      }}>
        <h2 style={{marginBottom: 8}}>Tableau de bord</h2>
        {employeeName && (
          <div style={{ fontSize: 16, color: '#555', marginBottom: 28 }}>{employeeName}</div>
        )}
        <div style={{marginBottom: 32, minHeight: 80, width: '100%'}}>
          <UnifiedNotificationBlock 
            absenceNotifications={notifications} 
            timesheetNotifications={timesheetNotifications}
            onConsulterAbsence={handleConsulterAbsence}
            onConsulterTimesheet={handleConsulterTimesheet}
          />
        </div>
        <div style={{display: 'flex', flexDirection: 'column', gap: 16, width: '100%'}}>
          <button className="history" onClick={() => navigate("/history")}>Historique de travail</button>
          <button className="history" onClick={() => navigate("/absence-request")}>Demande d'Absence</button>
          <button className="payslip" onClick={() => navigate("/payslips")}>Bulletins de Paie</button>
          <button 
            className="timesheet" 
            onClick={() => navigate("/timesheet")}
          >
            Feuille de Temps
          </button>
          <button className="dashboard-back" onClick={() => navigate("/")}>Retour</button>
        </div>
        {showAbsencePopup && (
          <AbsencePopup id={showAbsencePopup} onClose={() => setShowAbsencePopup(null)} />
        )}
        {showTimesheetPopup && (
          <TimesheetPopup id={showTimesheetPopup} onClose={() => setShowTimesheetPopup(null)} />
        )}
      </div>
    </div>
  );
}

export default EmployeeDashboardBoard;